/**
 * Turn the game state into the sentence a knowledgeable friend would say.
 *
 * This is the product (PRD §1): the user glances at the phone and reads
 * one line that tells them what is happening and why it matters. Pure
 * functions, so the wording itself is unit-testable.
 *
 * Team names come in through `speak`/`verb` helpers rather than being
 * looked up here, both to keep this module pure and so verb agreement is
 * right: "Buffalo needs 7 yards", but "the Jets need 7 yards".
 */

import { GAME_STATUS, yardsToEndZone } from "./gameEngine";

/** Roughly the edge of a reliable field goal: the opponent's 38 ≈ a 55-yard kick. */
const FIELD_GOAL_RANGE_POSITION = 62;

/**
 * The two or three words a badge uses for a status.
 *
 * Wording lives here rather than in gameEngine, which stores no
 * presentation strings (Implementation Guide §24), and it is one map
 * rather than one per component so a game cannot be "Postponed" on the
 * schedule and "Not started" on its own page.
 */
const STATUS_LABELS = Object.freeze({
  [GAME_STATUS.SCHEDULED]: "Not started",
  [GAME_STATUS.IN_PROGRESS]: "Live",
  [GAME_STATUS.FINAL]: "Final",
  [GAME_STATUS.POSTPONED]: "Postponed",
  [GAME_STATUS.CANCELLED]: "Called off",
  [GAME_STATUS.SUSPENDED]: "Stopped",
  [GAME_STATUS.DELAYED]: "Delayed",
  [GAME_STATUS.UNKNOWN]: "Status unclear",
});

/**
 * @param {string} status one of GAME_STATUS
 * @returns {string} never empty: a status we cannot name says so.
 */
export const gameStatusLabel = (status) =>
  STATUS_LABELS[status] ?? STATUS_LABELS[GAME_STATUS.UNKNOWN];

/**
 * The line a schedule card prints instead of a kickoff time.
 *
 * Only the interrupted states have one, because only they need to
 * replace something. Each says what happened and stops: none of them
 * promises a new date, an outcome, or a restart, because the feed tells
 * us none of those things.
 */
const STATUS_NOTES = Object.freeze({
  [GAME_STATUS.POSTPONED]: "Put off. We haven't been told a new date.",
  [GAME_STATUS.CANCELLED]: "This one isn't being played.",
  [GAME_STATUS.SUSPENDED]: "Stopped part-way through.",
  [GAME_STATUS.DELAYED]: "On hold for now.",
  [GAME_STATUS.UNKNOWN]: "We couldn't read the status of this game.",
});

/**
 * @param {string} status one of GAME_STATUS
 * @returns {string} the note, or the unknown note for anything unnamed.
 */
export const gameStatusNote = (status) =>
  STATUS_NOTES[status] ?? STATUS_NOTES[GAME_STATUS.UNKNOWN];

const yardWord = (n) => `${n} ${n === 1 ? "yard" : "yards"}`;

const DEFAULT_HELPERS = {
  speak: (teamId) => String(teamId ?? "the team"),
  verb: (_teamId, singular) => singular,
};

const idle = (headline, plain) => ({
  headline,
  plain,
  stakes: null,
  goalToGo: false,
  inFieldGoalRange: false,
  inRedZone: false,
});

/**
 * @param {object} state game state
 * @param {{speak: (id: string) => string, verb: (id: string, s: string, p: string) => string}} helpers
 * @returns {{
 *   headline: string,
 *   plain: string,
 *   stakes: string|null,
 *   goalToGo: boolean,
 *   inFieldGoalRange: boolean,
 *   inRedZone: boolean
 * }|null}
 */
export function describeSituation(state, helpers = DEFAULT_HELPERS) {
  if (!state) return null;

  const { speak, verb } = { ...DEFAULT_HELPERS, ...helpers };

  if (state.status === GAME_STATUS.SCHEDULED) {
    return idle(
      "Not started yet",
      "The game hasn't kicked off. Nothing to work out just yet."
    );
  }

  if (state.status === GAME_STATUS.FINAL) {
    return idle("Full time", "That's the end of the game.");
  }

  /*
   * The interrupted states. Each one gets its own sentence because
   * "Between plays" below would be an outright lie for all of them, and
   * because the difference between "another day" and "not at all" is the
   * only thing a viewer actually wants to know here. None of them claims
   * a new date: we are not told one.
   */
  if (state.status === GAME_STATUS.POSTPONED) {
    return idle(
      "Postponed",
      "This game has been put off. It isn't being played at the time it was listed for, and we haven't been told a new one."
    );
  }

  if (state.status === GAME_STATUS.CANCELLED) {
    return idle(
      "Called off",
      "This game isn't being played at all. Anything on the scoreboard is whatever the feed still carries, not a result."
    );
  }

  if (state.status === GAME_STATUS.SUSPENDED) {
    return idle(
      "Stopped",
      "The game has been stopped part-way through. The score is where it paused, not a final one."
    );
  }

  if (state.status === GAME_STATUS.DELAYED) {
    return idle(
      "Delayed",
      "The game is on hold. Nothing is happening on the field until it restarts."
    );
  }

  if (state.status === GAME_STATUS.UNKNOWN) {
    return idle(
      "Status unclear",
      "We can't tell what's happening in this game, so we'd rather say that than guess."
    );
  }

  if (!state.possession || !state.down) {
    return idle("Between plays", "Waiting for the next play to start.");
  }

  const team = speak(state.possession);
  const other = speak(otherTeam(state));
  const needs = verb(state.possession, "needs", "need");
  const has = verb(state.possession, "has", "have");
  const keeps = verb(state.possession, "keeps", "keep");
  const isAre = verb(state.possession, "is", "are");
  const otherTakes = verb(otherTeam(state), "takes", "take");

  const distance = state.distance;
  const toEndZone = yardsToEndZone(state);
  const goalToGo = state.ballPosition + distance >= 100;
  const inRedZone = toEndZone <= 20;
  const inFieldGoalRange = state.ballPosition >= FIELD_GOAL_RANGE_POSITION;

  const need = goalToGo
    ? `${team} ${isAre} ${yardWord(toEndZone)} from the end zone.`
    : `${team} ${needs} ${yardWord(distance)} for a first down, a fresh set of four tries.`;

  const plainByDown = {
    1: `${need} This is the first of four tries.`,
    2: `${need} They have three tries left.`,
    3: `${need} If this doesn't work there is one try left, and most teams kick the ball away rather than risk it.`,
    4: `${need} This is their last try.`,
  };

  const stakesByDown = {
    3: `Get it and ${team} ${keeps} the ball. Come up short, and it's fourth down, where the usual move is a punt.`,
    4: inFieldGoalRange
      ? `If this doesn't work, ${other} ${otherTakes} over right here. That's why a field goal is tempting: three points beats nothing.`
      : `If this doesn't work, ${other} ${otherTakes} over right here. That's why teams usually punt instead.`,
  };

  return {
    headline: `${team} ${has} the ball`,
    plain: plainByDown[state.down] ?? need,
    stakes: stakesByDown[state.down] ?? null,
    goalToGo,
    inFieldGoalRange,
    inRedZone,
  };
}

function otherTeam(state) {
  return state.possession === state.homeTeamId ? state.awayTeamId : state.homeTeamId;
}

/**
 * One-line summary of a completed play, for the feed.
 * Falls back to the scripted description when there is nothing to add.
 */
export function describePlayOutcome(event) {
  if (!event) return "";

  if (event.pointsScored === 6) return "Touchdown!";
  if (event.pointsScored === 3) return "Field goal: 3 points.";
  if (event.pointsScored === 2) return "Two-point conversion: 2 points.";
  if (event.pointsScored === 1) return "Extra point: 1 point.";
  if (event.turnover) return "The other team has the ball now.";
  if (event.firstDown) return "First down: four fresh tries.";

  return event.description ?? "";
}
